import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { YachtLogo } from '../components/YachtLogo';
import { Check, ArrowRight, Github, Mail, ShieldCheck, Zap, Layers, Loader2, X } from 'lucide-react';
import { auth, handleGoogleLogin } from '../firebase';
import { createCheckoutSession } from '../lib/stripe';
import { toast } from 'sonner';

import Navbar from '../components/Navbar';

export default function Pricing() {
  const [isUpgrading, setIsUpgrading] = useState(false);

  const handleUpgrade = async (tier: string) => {
    if (!auth.currentUser) {
      handleGoogleLogin();
      return;
    }
    
    setIsUpgrading(true);
    const toastId = toast.loading("Connecting to Stripe secure checkout...");
    try {
      const priceId = tier === 'founder' ? 'price_founder_token' : 'price_pro_lab'; 
      await createCheckoutSession(auth.currentUser.uid, priceId);
    } catch (error) {
      console.error("Payment failed:", error);
      toast.error("Bridge connection failed. Please try again.");
    } finally {
      setIsUpgrading(false);
      toast.dismiss(toastId);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-zinc-100 selection:text-black flex flex-col">
      <Navbar />

      <main className="flex-1 pt-48 pb-32 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-24">
             <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-7xl md:text-8xl font-mono uppercase tracking-tighter mb-8"
            >
              Ship your first app today.
            </motion.h1>
            <p className="text-zinc-500 text-xl font-light max-w-2xl mx-auto leading-relaxed">
              From idea to live product in one session. Choose your level of leverage.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {/* Free Tier */}
            <div className="p-10 border border-zinc-900 bg-zinc-950/50 rounded-[40px] flex flex-col hover:border-zinc-800 transition-all">
              <div className="flex-1">
                <h3 className="tech-label text-zinc-500 mb-6">Free</h3>
                <div className="flex items-baseline gap-2 mb-8">
                  <span className="text-5xl font-mono text-white">$0</span>
                </div>
                <p className="text-zinc-500 font-light text-sm leading-relaxed mb-10">Essential synthesis for curious builders and personal exploration.</p>
                <ul className="space-y-4 mb-12">
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600"><Check size={12} className="text-[#0074D9]" /> 1 Active Project</li>
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600"><Check size={12} className="text-[#0074D9]" /> 5 AI Generations</li>
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600 text-zinc-800"><X size={12} className="text-zinc-800" /> No Deployment</li>
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600"><Check size={12} className="text-[#0074D9]" /> Session-only history</li>
                </ul>
              </div>
              <Link to="/studio" className="w-full py-4 bg-zinc-900 text-zinc-400 rounded-2xl font-mono uppercase text-[10px] tracking-widest font-bold hover:bg-zinc-800 transition-all text-center">
                Start building
              </Link>
            </div>

            {/* Founder Tier */}
            <div className="p-10 border-2 border-[#0074D9] bg-[#001F3F]/10 rounded-[40px] flex flex-col relative scale-[1.02] shadow-[0_0_80px_rgba(0,116,217,0.1)]">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-[#0074D9] text-white text-[10px] font-mono uppercase tracking-[0.2em] font-bold rounded-full border-4 border-[#050505]">Most builders start here</div>
              <div className="flex-1">
                <h3 className="tech-label text-[#0074D9] mb-6">Founder</h3>
                <div className="flex items-baseline gap-2 mb-8">
                  <span className="text-5xl font-mono text-white">$29</span>
                  <span className="text-zinc-600 font-mono text-[10px] uppercase tracking-widest">/ first month</span>
                </div>
                <p className="text-zinc-400 font-light text-sm leading-relaxed mb-10">Best for shipping your first real app. Unlimited generations, zero friction.</p>
                <ul className="space-y-4 mb-12">
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-300 font-bold"><Check size={12} className="text-[#0074D9]" /> Unlimited generations</li>
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-300 font-bold"><Check size={12} className="text-[#0074D9]" /> up to 3 Active Projects</li>
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-300 font-bold"><Check size={12} className="text-[#0074D9]" /> Deploy apps live</li>
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-300 font-bold"><Check size={12} className="text-[#0074D9]" /> Yacht Labs badge included</li>
                </ul>
              </div>
              <button 
                onClick={() => handleUpgrade('founder')}
                disabled={isUpgrading}
                className="w-full py-5 bg-white text-black rounded-2xl font-mono uppercase text-xs tracking-[0.2em] font-bold hover:bg-zinc-200 transition-all flex items-center justify-center gap-2"
              >
                {isUpgrading ? <Loader2 className="animate-spin" size={14} /> : 'Finish your first app'}
              </button>
              <p className="mt-4 text-center text-[10px] font-mono uppercase text-zinc-600">Price increases after first build</p>
            </div>

            {/* Pro Tier */}
            <div className="p-10 border border-zinc-900 bg-zinc-950/50 rounded-[40px] flex flex-col hover:border-zinc-800 transition-all">
              <div className="flex-1">
                <h3 className="tech-label text-white mb-6">Pro</h3>
                <div className="flex items-baseline gap-2 mb-8">
                  <span className="text-5xl font-mono text-white">$99</span>
                  <span className="text-zinc-700 font-mono text-[10px] uppercase tracking-widest">/ month</span>
                </div>
                <p className="text-zinc-500 font-light text-sm leading-relaxed mb-10">Advanced capabilities for serious builders and scaling teams.</p>
                <ul className="space-y-4 mb-12">
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600"><Check size={12} className="text-[#0074D9]" /> Unlimited Everything</li>
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600"><Check size={12} className="text-[#0074D9]" /> No Yacht Labs Branding</li>
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600"><Check size={12} className="text-[#0074D9]" /> Priority build speed</li>
                  <li className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600"><Check size={12} className="text-[#0074D9]" /> Advanced engine features</li>
                </ul>
              </div>
              <button 
                onClick={() => handleUpgrade('pro')}
                disabled={isUpgrading}
                className="w-full py-4 bg-[#0A0A0A] border border-[#222] text-white rounded-2xl font-mono uppercase text-[10px] tracking-widest font-bold hover:bg-zinc-900 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUpgrading ? <Loader2 className="animate-spin" size={14} /> : 'Remove all limits'}
              </button>
            </div>
          </div>

          <div className="mt-32 pt-16 border-t border-zinc-900 grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 rounded-full border border-zinc-900 flex items-center justify-center text-[#0074D9]">
                <ShieldCheck size={20} />
              </div>
              <h5 className="text-[10px] font-mono uppercase tracking-widest">Founder Guarantee</h5>
              <p className="text-[9px] text-zinc-500 uppercase tracking-widest leading-relaxed">Not impressed? 100% refund within 7 days. No questions.</p>
            </div>
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 rounded-full border border-zinc-900 flex items-center justify-center text-[#0074D9]">
                <Zap size={20} />
              </div>
              <h5 className="text-[10px] font-mono uppercase tracking-widest">Safe & Secure</h5>
              <p className="text-[9px] text-zinc-500 uppercase tracking-widest leading-relaxed">Payments processed securely via Stripe. Data encrypted at rest.</p>
            </div>
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 rounded-full border border-zinc-900 flex items-center justify-center text-[#0074D9]">
                <Layers size={20} />
              </div>
              <h5 className="text-[10px] font-mono uppercase tracking-widest">Clean Architecture</h5>
              <p className="text-[9px] text-zinc-500 uppercase tracking-widest leading-relaxed">Your data is yours. Export full source code anytime. Zero lock-in.</p>
            </div>
          </div>
        </div>
      </main>

      <footer className="p-24 border-t border-zinc-900 bg-black">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-24">
          <div className="col-span-2 space-y-8">
            <Link to="/" className="flex items-center gap-4">
              <YachtLogo className="w-8 h-8" />
              <span className="font-mono text-2xl uppercase tracking-widest">Yacht Labs</span>
            </Link>
            <p className="text-zinc-500 font-light leading-relaxed max-w-sm">
              Synthesizing the future of digital architecture. Empowering founders to bridge the gap between intent and existence.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
